import { Outlet, useLocation } from 'react-router-dom'
import TransactionTable from '@/pages/home/TransactionTable'
import TopBar from '@/pages/home/TopBar'
import { AnimatePresence, motion } from 'framer-motion'
import SideBar from '@/pages/home/SideBar'
import { PANEL_STATES } from '@/data/constants/constants'
import useAppStore from '@/store/useAppStore'
import useGetVaults from '@/services/api/vault/useGetVaults.js'
import useGetPeriodColumns from '@/services/api/cash-flow/useGetPeriodColumns.js'
import useGetTransactions from '@/services/api/cash-flow/transaction/useGetTransactions.js'
import useGetTransactionCategories from '@/services/api/transaction-category/useGetTransactionCategories.ts'
import PropTypes from 'prop-types'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable'
import { useEffect, useRef } from 'react'

export default function LayoutHome({
  home = false,
  modal = false,
  modalWithRightPanel = false,
}) {
  LayoutHome.propTypes = {
    home: PropTypes.bool,
    modal: PropTypes.bool,
    modalWithRightPanel: PropTypes.bool,
  }

  const panelState = useAppStore((state) => state.panelState)

  const dockTransactionListView = useAppStore(
    (state) => state.dockTransactionListView,
  )

  const location = useLocation()

  const periodType = useAppStore((state) => state.periodType)
  const offset = useAppStore((state) => state.offset)
  const numberOfPeriods = useAppStore((state) => state.numberOfPeriods)

  const cellTransactionsLoaded = useAppStore(
    (state) => state.cellTransactionsLoaded,
  )

  const { status } = useGetVaults({
    offset,
    numberOfPeriods,
    periodType,
  })
  const { status: periodColumnsStatus } = useGetPeriodColumns({
    numberOfPeriods,
    offset,
    periodType,
  })

  const { status: transactionStatus } = useGetTransactions({
    offset,
    numberOfPeriods,
    periodType,
  })

  const { status: inStatues } = useGetTransactionCategories({ type: 'income' })
  const { status: outStatues } = useGetTransactionCategories({
    type: 'expenses',
  })

  const bottomPanelHeightRef = useRef(null)
  const bottomPanelRef = useRef(null)

  const elBottomPanel = document.getElementById('bottom-panel')

  useEffect(() => {
    if (elBottomPanel && dockTransactionListView && cellTransactionsLoaded) {
      elBottomPanel.style.maxHeight = 'max-content'
      elBottomPanel.style.minHeight = 'max-content'

      bottomPanelRef.current.resize(50)

      setTimeout(() => {
        const containerHeight = document.getElementById(
          'main-table-container',
        ).clientHeight
        const bottomPanelHeight = elBottomPanel.clientHeight
        // Flex basis ratio
        const resizeTo = (bottomPanelHeight / containerHeight) * 100 + 1
        bottomPanelRef.current.resize(resizeTo)

        elBottomPanel.style.maxHeight = 'unset'
        elBottomPanel.style.minHeight = 'unset'
      }, 100)
    } else {
      if (elBottomPanel) {
        elBottomPanel.style.maxHeight = 'max-content'
        elBottomPanel.style.minHeight = 'max-content'
      }
    }
  }, [dockTransactionListView, elBottomPanel, cellTransactionsLoaded])

  const success =
    periodColumnsStatus === 'success' &&
    transactionStatus === 'success' &&
    status === 'success' &&
    inStatues === 'success' &&
    outStatues === 'success'
  return (
    <div className="m-0 flex h-screen overflow-x-hidden">
      <div
        id="portal-middle"
        className="relative m-0 grid h-screen flex-1 grid-cols-[max-content_1fr] overflow-x-hidden"
      >
        {/* LEFT */}
        <div>
          <AnimatePresence>
            {panelState === PANEL_STATES.OPENED && (
              <motion.div
                initial={{
                  width: '0',
                  minWidth: '0',
                  overflowX: 'hidden',
                  whiteSpace: 'nowrap',
                }}
                animate={{
                  width: '280px',
                  minWidth: '280px',
                  overflowX: 'unset',
                  whiteSpace: 'unset',
                }}
                transition={{ duration: 0.2, delay: 0 }}
                exit={{
                  width: '0',
                  minWidth: '0',
                  overflowX: 'hidden',
                  whiteSpace: 'nowrap',
                  transition: { duration: 0.1, delay: 0 },
                }}
              >
                <SideBar />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="relative grid h-screen grid-rows-[max-content_1fr] overflow-hidden ">
          <div className="px-3 pt-3">
            <TopBar />
          </div>
          <div
            id="main-cont"
            className="grid grid-cols-[1fr_max-content] overflow-auto"
          >
            <div className=" relative w-full flex-1 overflow-x-hidden p-3">
              <div className=" relative h-full w-full">
                <ResizablePanelGroup
                  // key={searchParams.get('doc') ? 'true' : 'fff'}
                  id="main-table-container"
                  direction="vertical"
                  className="grid h-full w-full "
                >
                  <ResizablePanel>
                    <TransactionTable />
                  </ResizablePanel>

                  {modalWithRightPanel && (
                    <>
                      <ResizableHandle withHandle />
                      <ResizablePanel
                        ref={bottomPanelRef}
                        defaultSize={0}
                        // minSize={1}
                        maxSize={70}
                        id="bottom-panel"
                        className="grid"
                      >
                        <div id="new-container" ref={bottomPanelHeightRef}>
                          <Outlet />
                        </div>
                      </ResizablePanel>
                    </>
                  )}
                  {/* </div> */}
                </ResizablePanelGroup>
              </div>
            </div>

            {/* RIGHT */}
            {!home && success && !modal && !modalWithRightPanel && (
              <AnimatePresence>
                <motion.div
                  initial={{
                    width: '0',
                    overflow: 'hidden',
                    minWidth: '0',
                  }}
                  animate={{
                    width: '320px',
                    overflow: 'unset',
                    minWidth: '320px',
                  }}
                  transition={{ duration: 0.2 }}
                  exit={{
                    width: '0',
                    minWidth: '0',
                    transition: { duration: 0.1 },
                  }}
                  id="portal-right"
                  className="relative my-3"
                >
                  <Outlet key={`${location.pathname}-${location.search}`} />
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </div>
      </div>
      {modal && <Outlet key={`${location.pathname}-${location.search}`} />}
      {/* {modalWithRightPanel && <Outlet />} */}

      {/* {!home && success && !modal && !modalWithRightPanel && (
        <AnimatePresence>
          <motion.div
            initial={{ width: '0', overflow: 'hidden' }}
            animate={{ width: '320px', overflow: 'unset' }}
            transition={{ duration: 0.2 }}
            exit={{ width: '0', transition: { duration: 0.1 } }}
            id="portal-right"
            className="relative my-3"
          >
            <Outlet key={`${location.pathname}-${location.search}`} />
          </motion.div>
        </AnimatePresence>
      )} */}
    </div>
  )
}
